import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { DmProject, generateUploadUrl, updateDmProject, getPublicSignedUrl } from '../../api-services/dm-projects.client';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeHighlight from 'rehype-highlight';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import useConfirm from '../../hooks/useConfirm';
import { useToast } from '../../context/toast-context';


interface GeneratedReportTabProps {
    project: DmProject;
    responseLLM: any;
    setResponseLLM: (response: any) => void;
    fetchProject: () => void;
    setTab: (tab: number) => void;

}


const GeneratedReportTab: React.FC<GeneratedReportTabProps> = ({ project, responseLLM, fetchProject, setTab, setResponseLLM }) => {
    const { showToast } = useToast();
    const { projectId } = useParams<{ projectId: string }>();
    const { onConfirmMessage, confirmContainer } = useConfirm();


    const [isUploading, setIsUploading] = useState(false);
    const [reportUrl, setReportUrl] = useState<string>('');
    const [isLoadingUrl, setIsLoadingUrl] = useState(false);

    const markdown = responseLLM?.payload?.body?.response;
    const fileReportS3Key = project?.report?.s3Key;

    useEffect(() => {
        const fetchReportUrl = async () => {
            if (fileReportS3Key) {
                try {
                    setIsLoadingUrl(true);
                    const url = await getPublicSignedUrl(fileReportS3Key);
                    setReportUrl(url);
                } catch (error) {
                    console.error('Error fetching report URL:', error);
                    showToast({
                        message: 'Error loading report',
                        severity: 'error',
                    });
                } finally {
                    setIsLoadingUrl(false);
                }
            }
        };

        fetchReportUrl();
    }, [fileReportS3Key, showToast]);

    const uploadPDF = async () => {
        try {
            const confirm = await onConfirmMessage({
                message: 'Are you sure you want to save and upload the report?',
                title: 'Confirm',
            });
            if (!confirm) return;


            setIsUploading(true);

            // Generate PDF first
            const element = document.getElementById('generated-report-container');
            if (!element) {
                showToast({
                    message: 'Generated report container not found',
                    severity: 'error',
                });
                return;
            }

            const originalHeight = element.style.height;
            const originalOverflow = element.style.overflow;

            element.style.height = 'auto';
            element.style.minHeight = element.scrollHeight + 'px';
            element.style.maxHeight = 'none';
            element.style.overflow = 'visible';

            const canvas = await html2canvas(element, {
                scale: 1.1,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                width: element.scrollWidth,
                height: element.scrollHeight,
                onclone: (clonedDoc) => {
                    const clonedElement = clonedDoc.getElementById('generated-report-container');
                    if (clonedElement) {
                        clonedElement.style.padding = '20px';
                        clonedElement.style.backgroundColor = '#ffffff';
                        clonedElement.style.color = '#000000';
                        clonedElement.style.height = clonedElement.scrollHeight + 'px';
                        clonedElement.style.overflow = 'visible';
                    }
                }
            });

            const pdf = new jsPDF('p', 'mm', 'a3');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            const pxPerMm = canvas.width / (pdfWidth);
            const pageHeightPx = Math.floor(pdfHeight * pxPerMm);

            let renderedHeight = 0;
            let pageNum = 0;
            while (renderedHeight < canvas.height) {
                const pageCanvas = document.createElement('canvas');
                pageCanvas.width = canvas.width;
                pageCanvas.height = Math.min(pageHeightPx, canvas.height - renderedHeight);
                const ctx = pageCanvas.getContext('2d');
                if (ctx) {
                    ctx.drawImage(
                        canvas,
                        0,
                        renderedHeight,
                        canvas.width,
                        pageCanvas.height,
                        0,
                        0,
                        canvas.width,
                        pageCanvas.height
                    );
                }
                const imgData = pageCanvas.toDataURL('image/png');
                if (pageNum > 0) pdf.addPage();
                pdf.addImage(
                    imgData,
                    'PNG',
                    0,
                    0,
                    pdfWidth,
                    (pageCanvas.height * pdfWidth) / canvas.width
                );
                renderedHeight += pageHeightPx;
                pageNum++;
            }

            // Convert PDF to blob
            const pdfBlob = pdf.output('blob');
            const fileName = `report.pdf`;

            // Get upload URL
            const { url, key, fileName: uploadedFileName } = await generateUploadUrl(fileName, 'application/pdf', projectId, "PROJECT_REPORT");

            // Upload to S3
            const uploadRes = await axios.put(url, pdfBlob, {
                headers: {
                    'Content-Type': 'application/pdf',
                },
                onUploadProgress: (progressEvent: any) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    console.log(`uploading... ${percentCompleted}%`);
                },
            });

            if (uploadRes?.status === 200) {
                // update project report
                await updateDmProject(projectId, {
                    report: {
                        s3Key: key,
                        createdAt: Date.now(),
                        fileName: uploadedFileName,

                    },
                    status: 'DEAL_ROOM_READY'
                });

                showToast({
                    message: 'PDF uploaded successfully',
                    severity: 'success',
                });

                setResponseLLM(null);
                await fetchProject();
            } else {
                showToast({
                    message: 'Error uploading PDF',
                    severity: 'error',
                });
            }

            element.style.height = originalHeight;
            element.style.overflow = originalOverflow;

        } catch (error) {
            console.error('Error uploading PDF:', error);
            showToast({
                message: 'Error uploading PDF',
                severity: 'error',
            });
        } finally {
            setIsUploading(false);
        }
    };


    // If report exists, show it in iframe
    if (fileReportS3Key && !markdown) {
        return (
            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h4" fontWeight={700}>
                        Generated Report
                    </Typography>
                    <Box display="flex" gap={2}>
                        <Button
                            variant="contained"
                            color="secondary"
                            sx={{
                                background: '#6435C9',
                                color: '#fff',
                                borderRadius: 2,
                                fontWeight: 500,
                                '&:hover': { background: '#5127B0' },
                                '&:disabled': { background: '#9B7EDF' },
                            }}
                            onClick={() => setTab(0)}
                            disabled={isUploading}
                        >
                            Re-Generate
                        </Button>
                    </Box>
                </Box>

                {isLoadingUrl ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <CircularProgress />
                    </Box>
                ) : reportUrl ? (
                    <Box sx={{
                        background: '#fff',
                        borderRadius: 2,
                        boxShadow: 1,
                        overflow: 'hidden',
                        height: '80vh'
                    }}>
                        <iframe
                            src={reportUrl}
                            title="Generated Report"
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                        />
                    </Box>
                ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <Typography color="error">Failed to load report</Typography>
                    </Box>
                )}
            </Box>
        );
    }

    // If no report exists, show the generation interface
    return (
        <Box>
            {confirmContainer}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" fontWeight={700}>
                    Generated Report
                </Typography>
                <Box display="flex" gap={2}>
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{
                            background: '#6435C9',
                            color: '#fff',
                            borderRadius: 2,
                            fontWeight: 500,
                            '&:hover': { background: '#5127B0' },
                            '&:disabled': { background: '#9B7EDF' },
                        }}
                        onClick={() => setTab(0)}
                        disabled={isUploading}
                    >
                        Re-Generate
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{
                            background: '#6435C9',
                            color: '#fff',
                            borderRadius: 2,
                            fontWeight: 500,
                            '&:hover': { background: '#5127B0' },
                            '&:disabled': { background: '#9B7EDF' },
                        }}
                        onClick={uploadPDF}
                        disabled={isUploading || !markdown}
                    >
                        {isUploading ? 'Uploading...' : 'Save & Send to Deal Room'}
                    </Button>
                </Box>
            </Box>
            <Box id="generated-report-container" style={{ paddingBottom: '500px' }}>
                <Box display="flex" justifyContent="center" mb={3}>
                    <img
                        src="/logos/Perl-Logo-Wide-Black.png"
                        alt="Perl Logo"
                        style={{
                            maxWidth: '300px',
                            height: 'auto',
                            marginBottom: '20px'
                        }}
                    />
                </Box>
                <Box>
                    <ReactMarkdown
                        children={markdown || 'No response from LLM'}
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw, rehypeHighlight]}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default GeneratedReportTab; 
